<script setup>
import { ref, computed, h, useTemplateRef, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import { useAccount } from 'dashboard/composables/useAccount';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  generateAutomationPayload,
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

const props = defineProps({
  saving: Boolean,
  saveError: { type: String, default: '' },
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save']);
const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { isSmallScreen } = useSmallScreen();
const { operators } = useOperators();

const dialogRef = ref(null);
const conditionsRef = useTemplateRef('conditionsRef');
const errors = ref({});
const discardRef = ref(null);
const eventChangeRef = ref(null);
const pendingEvent = ref(null);
const baseline = ref('');
const snapshot = () => JSON.stringify(automation.value);
const isDirty = computed(() => baseline.value !== snapshot());

const isEditMode = computed(() => props.mode === 'edit');

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const cancelKey = computed(() =>
  isEditMode.value
    ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT'
    : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const hasAutomationMutated = computed(() => {
  return Boolean(
    automation.value?.conditions[0]?.values ||
      automation.value?.actions[0]?.action_params?.length
  );
});

const automationActionTypes = computed(() => {
  const actionTypes = isCloudFeatureEnabled('sla')
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

  return actionTypes.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
  }));
});

const hasConditionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('condition_'))
);

const hasActionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('action_'))
);

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const isConditionsValid = () => {
  if (!conditionsRef.value) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const resetValidation = () => {
  errors.value = {};
  conditionsRef.value?.forEach(c => c.resetValidation());
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

const open = async () => {
  await nextTick();
  baseline.value = snapshot();
  resetValidation();
  dialogRef.value?.open();
};

const close = () => {
  resetValidation();
  dialogRef.value?.close();
};

const applyEventChange = () => {
  automation.value.event_name = pendingEvent.value;
  props.onEventChange();
  eventChangeRef.value?.close();
  pendingEvent.value = null;
};
const requestEventChange = event => {
  pendingEvent.value = event.target.value;
  event.target.value = automation.value.event_name;
  if (pendingEvent.value === automation.value.event_name) return;
  if (
    isEditMode.value ||
    hasAutomationMutated.value ||
    automation.value.conditions.length > 1 ||
    automation.value.actions.length > 1
  ) {
    eventChangeRef.value?.open();
  } else applyEventChange();
};

const requestClose = () => {
  if (props.saving) return;
  if (isDirty.value) discardRef.value?.open();
  else close();
};
const discard = () => {
  discardRef.value?.close();
  close();
};

const emitSaveAutomation = () => {
  if (props.saving) return;
  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    emit('save', payload, props.mode);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    :title="$t(titleKey)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    confirm-before-close
    @request-close="requestClose"
    @confirm="emitSaveAutomation"
  >
    <div v-if="automation" class="flex w-full flex-col gap-5" :inert="saving">
      <section :aria-labelledby="`${mode}-automation-basics`" class="space-y-3">
        <h3
          :id="`${mode}-automation-basics`"
          class="text-sm font-semibold text-n-slate-12"
        >
          {{ $t('AUTOMATION.EDITOR.BASICS') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('AUTOMATION.EDITOR.REQUIRED_FIELDS') }}
        </p>
        <Input
          :id="`${mode}-automation-name`"
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          custom-input-class="!h-11"
          :message-type="errors.name ? 'error' : 'info'"
          :message="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <Input
          :id="`${mode}-automation-description`"
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          custom-input-class="!h-11"
          :message-type="errors.description ? 'error' : 'info'"
          :message="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />
        <div>
          <label
            :for="`${mode}-automation-event`"
            class="mb-1 block text-sm font-medium text-n-slate-12"
          >
            <span>{{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}</span>
          </label>
          <div :class="{ error: errors.event_name }">
            <select
              :id="`${mode}-automation-event`"
              :value="automation.event_name"
              :aria-invalid="!!errors.event_name"
              class="m-0 h-11 w-full rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
              @change="requestEventChange"
            >
              <option
                v-for="event in automationRuleEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.value }}
              </option>
            </select>
            <span v-if="errors.event_name" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </div>
          <p
            v-if="!isEditMode && hasAutomationMutated"
            class="text-xs text-right text-n-teal-10 pt-1"
          >
            {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
          </p>
        </div>
      </section>
      <!-- Conditions Start -->
      <section
        :aria-labelledby="`${mode}-automation-conditions`"
        class="space-y-3"
      >
        <h3
          :id="`${mode}-automation-conditions`"
          class="text-sm font-semibold text-n-slate-12"
        >
          {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('AUTOMATION.EDITOR.CONDITIONS_HELP') }}
        </p>
        <ul
          class="grid gap-4 list-none p-3 mb-4 outline outline-1 rounded-2xl -outline-offset-1"
          :class="
            hasConditionErrors
              ? 'outline-n-ruby-5 bg-n-ruby-2/50'
              : 'outline-n-weak dark:outline-n-strong'
          "
        >
          <template v-for="(condition, i) in automation.conditions" :key="i">
            <ConditionRow
              v-if="i === 0"
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:values="automation.conditions[i].values"
              :contained="isSmallScreen"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(i)"
            />
            <ConditionRow
              v-else
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:query-operator="
                automation.conditions[i - 1].query_operator
              "
              v-model:values="automation.conditions[i].values"
              :contained="isSmallScreen"
              :filter-types="filterTypes"
              show-query-operator
              @remove="removeFilter(i)"
            />
          </template>
          <div>
            <NextButton
              icon="i-ri-add-line"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
              @click="appendNewCondition"
            />
          </div>
        </ul>
      </section>
      <!-- Conditions End -->
      <!-- Actions Start -->
      <section
        :aria-labelledby="`${mode}-automation-actions`"
        class="space-y-3"
      >
        <h3
          :id="`${mode}-automation-actions`"
          class="text-sm font-semibold text-n-slate-12"
        >
          {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('AUTOMATION.EDITOR.ACTIONS_HELP') }}
        </p>
        <ul
          class="grid list-none p-3 mb-4 outline outline-1 rounded-2xl -outline-offset-1 border-solid"
          :class="
            hasActionErrors
              ? 'outline-n-ruby-5 bg-n-ruby-2/50'
              : 'outline-n-weak dark:outline-n-strong'
          "
        >
          <AutomationActionInput
            v-for="(action, i) in automation.actions"
            :key="i"
            v-model="automation.actions[i]"
            :contained="isSmallScreen"
            :action-types="automationActionTypes"
            dropdown-max-height="max-h-[7.5rem]"
            :dropdown-values="getActionDropdownValues(action.action_name)"
            :show-action-input="
              showActionInput(automationActionTypes, action.action_name)
            "
            :error-message="
              errors[`action_${i}`]
                ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                : ''
            "
            :initial-file-name="
              isEditMode ? getFileName(action, automation.files) : ''
            "
            @reset-action="resetAction(i)"
            @remove-action="removeAction(i)"
          />
          <div class="pt-2">
            <NextButton
              icon="i-ri-add-line"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
              @click="appendNewAction"
            />
          </div>
        </ul>
      </section>
      <!-- Actions End -->
      <div class="w-full border-t border-n-weak">
        <p v-if="saveError" class="mt-3 text-sm text-n-ruby-11" role="alert">
          {{ saveError }}
        </p>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-4">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t(cancelKey)"
            @click.prevent="requestClose"
          />
          <NextButton
            solid
            blue
            type="submit"
            :label="$t(submitKey)"
            :disabled="saving"
            :is-loading="saving"
          />
        </div>
      </div>
    </div>
  </Dialog>
  <Dialog
    ref="eventChangeRef"
    type="alert"
    :title="$t('AUTOMATION.EDITOR.EVENT_CHANGE_TITLE')"
    :description="$t('AUTOMATION.EDITOR.EVENT_CHANGE_DESCRIPTION')"
    :cancel-button-label="$t('AUTOMATION.EDITOR.KEEP_EDITING')"
    :confirm-button-label="$t('AUTOMATION.EDITOR.CHANGE_EVENT')"
    @confirm="applyEventChange"
  />
  <Dialog
    ref="discardRef"
    type="alert"
    :title="$t('AUTOMATION.EDITOR.DISCARD_TITLE')"
    :description="$t('AUTOMATION.EDITOR.DISCARD_DESCRIPTION')"
    :cancel-button-label="$t('AUTOMATION.EDITOR.KEEP_EDITING')"
    :confirm-button-label="$t('AUTOMATION.EDITOR.DISCARD')"
    @confirm="discard"
  />
</template>
